/**
 * 적립 요청이 실패했을 때 클라이언트가 취할 동작.
 * - RETRY_SAME_KEY: 서버가 지급 여부를 아직 모른다. 같은 Idempotency-Key 로 다시 보내 결과를 받는다
 * - RESTART_CYCLE: 이번 시도는 끝났다. 키를 버리고 다음 주기를 기다린다
 * - COMPLETE: 이번 달 한도를 채웠다. 완료 안내 후 주기를 멈춘다
 * - STOP: 더 이상 적립할 수 없는 방송이다. 주기를 멈춘다
 */
export type AccrualFailureAction =
  | "RETRY_SAME_KEY"
  | "RESTART_CYCLE"
  | "COMPLETE"
  | "STOP"

/**
 * HTTP 200 이지만 `isSuccess: false` 일 때 body 의 statusCode (페이레터 거절 코드).
 * 월 한도 초과(1004)만 끝난 상태이고, 회원 없음(1003)·지급 제한(1005) 등은
 * 일시적일 수 있어 다음 주기에 다시 시도한다
 */
const REJECTED_CODE = {
  /** 월 한도 초과 */
  MONTHLY_LIMIT: 1004,
} as const

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  REQUEST_TIMEOUT: 408,
  CONFLICT: 409,
} as const

/*
 * 서버가 같은 상태코드에 다른 상황을 싣는 경우(400, 409)는 메시지로만 구분할 수 있다.
 * 서버 문구가 바뀌면 여기를 같이 바꿔야 한다. (wownet-api CoinController, CoinPaymentFacade)
 */
const MESSAGE = {
  /** 409: 같은 키의 요청을 서버가 아직 처리하고 있다 */
  IN_PROGRESS: "처리 중",
  /** 400: 지급 가능한 월 최대 금액 초과 */
  MONTHLY_LIMIT: "월 최대 금액",
  /** 400: 유료 방송은 지급할 수 없다 */
  PAID_SCHEDULE: "유료 방송",
} as const

/** 200 + `isSuccess: false` 응답을 분류한다 */
export const classifyRejected = (statusCode?: number): AccrualFailureAction => {
  switch (statusCode) {
    case REJECTED_CODE.MONTHLY_LIMIT:
      return "COMPLETE"
    default:
      return "RESTART_CYCLE"
  }
}

/** HTTP 에러를 분류한다. status 가 없으면 서버 응답을 받지 못한 것이다 */
export const classifyHttpError = (
  status?: number,
  message?: string,
): AccrualFailureAction => {
  if (status === undefined) return "RETRY_SAME_KEY"

  switch (status) {
    case HTTP_STATUS.REQUEST_TIMEOUT:
      return "RETRY_SAME_KEY"
    case HTTP_STATUS.CONFLICT:
      return message?.includes(MESSAGE.IN_PROGRESS)
        ? "RETRY_SAME_KEY"
        : "RESTART_CYCLE"
    case HTTP_STATUS.BAD_REQUEST:
      if (message?.includes(MESSAGE.MONTHLY_LIMIT)) return "COMPLETE"
      // 방송 도중 유료로 바뀐 경우. 타이머가 이미 꺼진 상태라 다음 주기를 걸지 않는다
      if (message?.includes(MESSAGE.PAID_SCHEDULE)) return "STOP"
      return "RESTART_CYCLE"
    default:
      return "RESTART_CYCLE"
  }
}
